import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Paper } from "@material-ui/core";
import Empty from "antd/es/empty";
import { Button, Modal, Table } from "../../components";
import FiledCopiesDocuments from "../../components/FiledCopiesDocuments/FiledCopiesDocuments";
import CompleteServiceFiledCopiesModal from "../../components/CompleteServiceFiledCopiesModal/CompleteServiceFiledCopiesModal";
import ClientServiceRequestForm from "../../components/ClientServiceRequestForm/ClientServiceRequestForm";
import { CLIENT_ACTION } from "../../redux/action";
import { dateTimeFormatter, Toaster } from "../../utils/common";
import {
  filterRecordsByClientCategory,
  normalizeClientDetailCategory,
} from "../../utils/clientDetailsCategory";
import {
  formatServiceStatus,
  getServiceBusinessName,
  getServiceCategoryLabel,
  getServiceClientBusinessId,
  normalizeServiceStatus,
  SERVICE_COMPLETED_STATUS,
  SERVICE_DEFAULT_STATUS,
} from "../../constants/clientServiceOptions";
import "./clientServicesSection.scss";

const BASE_TABLE_HEADERS = [
  { id: "sno", label: "S.No" },
  { id: "start_date", label: "Start Date" },
  { id: "service_name", label: "Service Name" },
  { id: "tax_year", label: "Tax Year" },
  { id: "service_frequency", label: "Service Frequency" },
  { id: "status", label: "Status" },
  { id: "updated_at", label: "Last Updated Date" },
];

const formatStartDate = (row) => {
  const raw = row?.start_date || row?.startDate;
  if (!raw) return "—";
  const text = String(raw);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

export default function ClientServicesSection({
  clientId,
  category = "individual",
  initialStatusTab,
  initialEditingServiceId,
}) {
  const normalizedCategory = normalizeClientDetailCategory(category);
  const categoryLabel = getServiceCategoryLabel(normalizedCategory);

  const [statusTab, setStatusTab] = useState(
    initialStatusTab === "closed" ? "closed" : "ongoing",
  );
  // Track whether category has changed after initial mount so we only reset
  // statusTab on a real category switch, not on first render.
  const prevCategoryRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clientBusinesses, setClientBusinesses] = useState([]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState(null);
  const [editServiceId, setEditServiceId] = useState(null);
  const [completeForm, setCompleteForm] = useState(null);

  const loadServices = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await CLIENT_ACTION.getClientServices({
        client_id: clientId,
        status: statusTab,
        service_category: normalizedCategory,
      });
      const list = Array.isArray(data) ? data : [];
      setRows(filterRecordsByClientCategory(list, normalizedCategory));
    } finally {
      setLoading(false);
    }
  }, [clientId, statusTab, normalizedCategory]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useEffect(() => {
    if (!clientId || normalizedCategory !== "business") {
      setClientBusinesses([]);
      return undefined;
    }
    let cancelled = false;
    CLIENT_ACTION.getClientBusinesses(clientId).then((list) => {
      if (!cancelled) setClientBusinesses(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, [clientId, normalizedCategory]);

  useEffect(() => {
    if (prevCategoryRef.current === null) {
      // First render — do not reset; respect initialStatusTab.
      prevCategoryRef.current = normalizedCategory;
      return;
    }
    if (prevCategoryRef.current !== normalizedCategory) {
      prevCategoryRef.current = normalizedCategory;
      setStatusTab("ongoing");
    }
  }, [normalizedCategory]);

  const showFiledCopies = statusTab === "closed";
  const tableHeaders = useMemo(() => {
    const headers = [...BASE_TABLE_HEADERS];
    if (normalizedCategory === "business") {
      headers.splice(3, 0, { id: "business_name", label: "Business" });
    }
    const statusIndex = headers.findIndex((header) => header.id === "status");
    if (showFiledCopies) {
      headers.splice(statusIndex + 1, 0, {
        id: "filed_copies",
        label: "Filed Copies",
      });
    } else {
      headers.push({ id: "", label: "Action" });
    }
    return headers;
  }, [normalizedCategory, showFiledCopies]);

  const tableData = useMemo(
    () =>
      rows.map((row, index) => ({
        sno: index + 1,
        start_date: formatStartDate(row),
        service_name: row.service_name || row.serviceName || "—",
        ...(normalizedCategory === "business"
          ? { business_name: getServiceBusinessName(row, clientBusinesses) }
          : {}),
        tax_year: row.tax_year || row.taxYear || "—",
        service_frequency: row.service_frequency || row.serviceFrequency || "—",
        status: formatServiceStatus(row.status),
        ...(showFiledCopies
          ? {
              filed_copies: (
                <FiledCopiesDocuments
                  documents={row.filed_copies || row.filedCopies}
                />
              ),
            }
          : {}),
        updated_at: dateTimeFormatter(
          row.updatedAt || row.updated_at || row.last_updated_at,
        ),
        client_service_id:
          row?.client_service_id ?? row?.clientServiceId ?? row?.id ?? row?.service_id ?? "",
        client_business_id: getServiceClientBusinessId(row),
        enabledActions: {
          edit: String(row?.status || "").toLowerCase() !== "completed",
        },
      })),
    [rows, clientBusinesses, normalizedCategory, showFiledCopies],
  );

  const openEditForRow = useCallback((row) => {
    const serviceId =
      row?.client_service_id ??
      row?.clientServiceId ??
      row?.id ??
      row?.service_id ??
      "";

    if (!serviceId) return;

    if (String(row?.status || "").toLowerCase() === "completed") return;

    setEditServiceId(String(serviceId));
    setEditInitialValues({
      start_date: row.start_date,
      service_name: row.service_name,
      tax_year: row.tax_year,
      service_frequency: row.service_frequency,
      client_business_id: row.client_business_id,
      status: normalizeServiceStatus(row.status),
    });
    setEditModalOpen(true);
  }, []);

  const handleCreateService = async (form) => {
    if (!form?.service_name || !form?.tax_year || !form?.service_frequency) {
      Toaster.warning("Please complete all service fields.");
      return;
    }

    setSubmitting(true);
    try {
      const employeeId = localStorage.getItem("user_id");
      const res = await CLIENT_ACTION.requestClientService({
        client_id: clientId,
        start_date: form.start_date,
        service_category: normalizedCategory,
        service_name: form.service_name,
        tax_year: form.tax_year,
        service_frequency: form.service_frequency,
        status: SERVICE_DEFAULT_STATUS,
        is_onboarding: false,
        created_by_employee_id: employeeId,
        ...(normalizedCategory === "business"
          ? { client_business_id: form.client_business_id }
          : {}),
      });
      if (res?.status === 1) {
        Toaster.success(res.message || "Service created successfully");
        setCreateModalOpen(false);
        setStatusTab("ongoing");
        setLoading(true);
        try {
          const data = await CLIENT_ACTION.getClientServices({
            client_id: clientId,
            status: "ongoing",
            service_category: normalizedCategory,
          });
          const list = Array.isArray(data) ? data : [];
          setRows(filterRecordsByClientCategory(list, normalizedCategory));
        } finally {
          setLoading(false);
        }
      } else {
        Toaster.error(res?.message || "Could not create service");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper className="client-services-section" elevation={0}>
      <div className="client-services-section__head">
        <div>
        </div>
        <div className="client-services-section__actions">
          <div className="status-toggle">
            <button
              type="button"
              className={`toggle-btn ${statusTab === "ongoing" ? "active" : ""}`}
              onClick={() => setStatusTab("ongoing")}
            >
              Ongoing
            </button>
            <button
              type="button"
              className={`toggle-btn ${statusTab === "closed" ? "active" : ""}`}
              onClick={() => setStatusTab("closed")}
            >
              Closed
            </button>
          </div>
          <Button
            text="Create service"
            handleClick={() => setCreateModalOpen(true)}
          />
        </div>
      </div>

      {loading ? (
        <p className="client-services-section__loading">Loading services...</p>
      ) : tableData.length === 0 ? (
        <Empty description={`No ${categoryLabel.toLowerCase()} services found`} />
      ) : (
        <Table
          variant="small"
          header={tableHeaders}
          data={tableData}
          aligncss="my-services-table"
          actions={showFiledCopies ? [] : ["edit"]}
          onActionClick={(action, rowData) => {
            if (action !== "edit") return;
            openEditForRow(rowData);
          }}
          restrictActionIcon
          hiddenFields={["client_service_id", "client_business_id"]}
        />
      )}

      {createModalOpen && (
        <Modal
          title={null}
          open
          handleClose={() => setCreateModalOpen(false)}
          className="client-services-section__modal timesheet-view-modal"
        >
          <div className="my-services-modal-content">
            <div className="my-services-modal-header">
              <h3>Create {categoryLabel.toLowerCase()} service</h3>
              <button
                type="button"
                className="my-services-modal-close"
                onClick={() => setCreateModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ClientServiceRequestForm
              defaultCategory={normalizedCategory}
              lockCategory
              clientId={clientId}
              businesses={clientBusinesses}
              submitting={submitting}
              submitLabel="Create service"
              onSubmit={handleCreateService}
              onCancel={() => setCreateModalOpen(false)}
            />
          </div>
        </Modal>
      )}

      {editModalOpen && editInitialValues && (
        <Modal
          title={null}
          open
          handleClose={() => {
            if (editSubmitting) return;
            setEditModalOpen(false);
          }}
          className="client-services-section__modal timesheet-view-modal"
        >
          <div className="my-services-modal-content">
            <div className="my-services-modal-header">
              <h3>Edit {categoryLabel.toLowerCase()} service</h3>
              <button
                type="button"
                className="my-services-modal-close"
                onClick={() => !editSubmitting && setEditModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ClientServiceRequestForm
              defaultCategory={normalizedCategory}
              lockCategory
              clientId={clientId}
              businesses={clientBusinesses}
              submitting={editSubmitting}
              submitLabel="Update service"
              showStatus
              initialValues={editInitialValues}
              onSubmit={async (form) => {
                if (!editServiceId) return;
                const nextStatus = normalizeServiceStatus(form.status);
                if (nextStatus === SERVICE_COMPLETED_STATUS) {
                  setCompleteForm(form);
                  return;
                }
                setEditSubmitting(true);
                try {
                  const res = await CLIENT_ACTION.updateClientService({
                    client_id: clientId,
                    client_service_id: editServiceId,
                    start_date: form.start_date,
                    service_category: normalizedCategory,
                    service_name: form.service_name,
                    tax_year: form.tax_year,
                    service_frequency: form.service_frequency,
                    status: nextStatus,
                    ...(normalizedCategory === "business"
                      ? { client_business_id: form.client_business_id }
                      : {}),
                  });

                  if (res?.status === 1) {
                    Toaster.success(res.message || "Service updated");
                    setEditModalOpen(false);
                    setEditInitialValues(null);
                    setEditServiceId(null);
                    if (nextStatus === SERVICE_COMPLETED_STATUS) {
                      setStatusTab("closed");
                    } else {
                      await loadServices();
                    }
                  } else {
                    Toaster.error(res?.message || "Could not update service");
                  }
                } finally {
                  setEditSubmitting(false);
                }
              }}
              onCancel={() => {
                if (editSubmitting) return;
                setEditModalOpen(false);
              }}
            />
          </div>
        </Modal>
      )}

      <CompleteServiceFiledCopiesModal
        open={Boolean(completeForm)}
        clientId={clientId}
        clientServiceId={editServiceId}
        serviceCategory={normalizedCategory}
        clientBusinessId={completeForm?.client_business_id}
        submitting={editSubmitting}
        onClose={() => {
          if (editSubmitting) return;
          setCompleteForm(null);
        }}
        onSubmit={async (documentIds) => {
          if (!editServiceId || !completeForm) return;
          setEditSubmitting(true);
          try {
            const res = await CLIENT_ACTION.updateClientService({
              client_id: clientId,
              client_service_id: editServiceId,
              start_date: completeForm.start_date,
              service_category: normalizedCategory,
              service_name: completeForm.service_name,
              tax_year: completeForm.tax_year,
              service_frequency: completeForm.service_frequency,
              status: SERVICE_COMPLETED_STATUS,
              document_ids: documentIds,
              ...(normalizedCategory === "business"
                ? { client_business_id: completeForm.client_business_id }
                : {}),
            });
            if (res?.status === 1) {
              Toaster.success(res.message || "Service updated");
              setCompleteForm(null);
              setEditModalOpen(false);
              setEditInitialValues(null);
              setEditServiceId(null);
              setStatusTab("closed");
            } else {
              Toaster.error(res?.message || "Could not update service");
            }
          } finally {
            setEditSubmitting(false);
          }
        }}
      />
    </Paper>
  );
}
